import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Zap, AlertCircle } from "lucide-react";
import { fmtCurrency, fmtRelative, fmtCost } from "@/lib/format";

const SOURCE_LABEL = {
  salesforce:      { label: "SF + pipeline",  class: "bg-emerald-50 text-emerald-800 ring-emerald-200" },
  salesforce_only: { label: "SF only",        class: "bg-zinc-100 text-zinc-700 ring-zinc-200" },
  pipeline:        { label: "Pipeline",       class: "bg-accent-soft text-accent ring-1",
                     style: { borderColor: "hsl(var(--accent-primary-ring))" } },
};

export default function OpportunitiesPage() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [stageFilter, setStage] = useState("__all__");
  const [sourceFilter, setSource] = useState("__all__");
  const nav = useNavigate();

  useEffect(() => { api.get("/opportunities").then((r) => setData(r.data)); }, []);

  const stages = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.items.map((o) => o.stage).filter(Boolean))].sort();
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const qn = q.trim().toLowerCase();
    return data.items
      .filter((o) => {
        if (stageFilter !== "__all__" && o.stage !== stageFilter) return false;
        if (sourceFilter !== "__all__" && o.source !== sourceFilter) return false;
        if (!qn) return true;
        return (o.name || "").toLowerCase().includes(qn)
            || (o.account_name || "").toLowerCase().includes(qn)
            || (o.opportunity_id || "").toLowerCase().includes(qn);
      })
      // DataTable expects stable `id` for selection/test ids
      .map((o) => ({ ...o, id: o.opportunity_id }));
  }, [data, q, stageFilter, sourceFilter]);

  const totalAmount = useMemo(
    () => filtered.reduce((s, o) => s + (o.amount || 0), 0),
    [filtered]
  );

  const columns = useMemo(() => [
    {
      accessorKey: "source",
      header: "Source",
      size: 130,
      cell: ({ getValue }) => {
        const src = SOURCE_LABEL[getValue()] || SOURCE_LABEL.salesforce_only;
        return (
          <span
            className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ring-inset ${src.class}`}
            style={src.style}
          >
            {src.label}
          </span>
        );
      },
    },
    {
      accessorKey: "name",
      header: "Name",
      size: 380,
      cell: ({ row }) => (
        <div className="truncate">
          <div className="strong truncate">{row.original.name || row.original.opportunity_id}</div>
          <div className="subtext mono truncate">{row.original.opportunity_id}</div>
        </div>
      ),
    },
    { accessorKey: "account_name", header: "Account", size: 200,
      cell: ({ getValue }) => getValue() || <span className="meta">—</span> },
    { accessorKey: "stage", header: "Stage", size: 150,
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? <span className="mono" style={{ fontSize: 13 }}>{v}</span> : <span className="meta">—</span>;
      } },
    {
      accessorKey: "amount",
      header: "Amount",
      meta: { align: "right" }, size: 120,
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? <span className="num strong">{fmtCurrency(v)}</span> : <span className="meta">—</span>;
      },
    },
    { accessorKey: "probability", header: "Prob", meta: { align: "right" }, size: 70,
      cell: ({ getValue }) => <span className="num meta">{getValue() ?? "—"}</span> },
    { accessorKey: "close_date", header: "Close", size: 120,
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? <span className="mono" style={{ fontSize: 13 }}>{v}</span> : <span className="meta">—</span>;
      } },
    { accessorKey: "source_email_count", header: "Emails", meta: { align: "center" }, size: 80,
      cell: ({ getValue }) => <span className="num meta">{getValue() || 0}</span> },
    {
      accessorKey: "llm_cost_usd",
      header: "LLM $", meta: { align: "right" }, size: 90,
      cell: ({ row }) => (
        <span className="num meta" title={row.original.llm_input_tokens
          ? `${row.original.llm_input_tokens} in / ${row.original.llm_output_tokens} out` : ""}>
          {fmtCost(row.original.llm_cost_usd)}
        </span>
      ),
    },
    {
      accessorKey: "last_touch", header: "Last touch", size: 130,
      cell: ({ getValue }) => {
        const v = getValue();
        return <span className="meta mono" style={{ fontSize: 12 }}>{v ? fmtRelative(v) : "—"}</span>;
      },
    },
  ], []);

  return (
    <div className="p-8 max-w-[1700px]" data-testid="opportunities-page">
      <PageHeader
        eyebrow="Salesforce pipeline"
        title="Opportunities"
        subtitle={data ? `${filtered.length} shown · ${fmtCurrency(totalAmount, true)}` : "Derived from Salesforce (live via MCP) + pipeline touches"}
        actions={data?.salesforce && <SfStatus sf={data.salesforce} />}
      />

      <div className="card-surface p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="opp-filters">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
          <Input
            placeholder="Search name, account, SF id…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-9 pl-8 text-sm"
            data-testid="opp-search-input"
          />
        </div>

        <Select value={stageFilter} onValueChange={setStage}>
          <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="opp-stage-filter">
            <SelectValue placeholder="Stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Stage — any</SelectItem>
            {stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={sourceFilter} onValueChange={setSource}>
          <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="opp-source-filter">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Source — any</SelectItem>
            <SelectItem value="salesforce">SF + pipeline</SelectItem>
            <SelectItem value="pipeline">Pipeline only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        onRowClick={(o) => nav(`/opportunities/${o.opportunity_id}`)}
        getRowTestId={(o) => `opp-row-${o.opportunity_id}`}
        initialSorting={[{ id: "last_touch", desc: true }]}
        emptyMessage="No opportunities match."
      />
    </div>
  );
}

function SfStatus({ sf }) {
  if (!sf.enabled) {
    return <span className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground"><AlertCircle size={12} /> SF MCP disabled</span>;
  }
  if (sf.error) {
    return <span className="flex items-center gap-1.5 text-xs font-mono text-rose-700" title={sf.error}><AlertCircle size={12} /> SF MCP error</span>;
  }
  return (
    <span className="flex items-center gap-1.5 text-xs font-mono text-accent">
      <Zap size={12} /> Live · {sf.sf_record_count} SF records
    </span>
  );
}
